import { Button, Box } from '@mui/material';
import { isEmpty } from 'lodash';
import { MouseEvent, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';

import PaymentTypeButton from '../../components/PaymentTypeButton';
import { PaymentType } from '../../constants/enums';
import { usePaymentsContext } from '../../store/payments';
import AccountPaymentTypeForm from './AccountPaymentTypeForm';
import CardPaymentTypeForm from './CardPaymentTypeForm';
import PaymentPage from './components/PaymentPage';

const AddPaymentMethodPage = () => {
  const [selectedPaymentType, setSelectedPaymentType] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = usePaymentsContext();
  const isSinglePayment = !isEmpty(state.singleSelectedPayment);
  const isPayMultiplePolicies = !isEmpty(state.selectedPayments);

  const paymentOptionSelected = isSinglePayment || isPayMultiplePolicies;

  const handleFormSubmit = () => {
    navigate('/payment/review', { state: location.state });
  };

  const renderContent = (): React.ReactElement => {
    switch (selectedPaymentType) {
      case PaymentType.CREDIT:
        return (
          <CardPaymentTypeForm
            onSubmit={handleFormSubmit}
            showNicknameField
            cancelAddPaymentMethod={() => setSelectedPaymentType('')}
            buttonText="Continue"
            showSaveToWalletCheckbox
          />
        );
      case PaymentType.CHECKING:
      case PaymentType.SAVINGS:
        return (
          <AccountPaymentTypeForm
            onSubmit={handleFormSubmit}
            accountType={selectedPaymentType}
            showNicknameField
            cancelAddPaymentMethod={() => setSelectedPaymentType('')}
            buttonText="Continue"
            showSaveToWalletCheckbox
          />
        );
      default:
        return (
          <>
            <PaymentTypeButton
              data-testid="creditTypeButton"
              onClick={() => setSelectedPaymentType(PaymentType.CREDIT)}
            >
              Debit/credit card
            </PaymentTypeButton>
            <PaymentTypeButton
              data-testid="checkingTypeButton"
              onClick={() => setSelectedPaymentType(PaymentType.CHECKING)}
            >
              Checking account
            </PaymentTypeButton>
            <PaymentTypeButton
              data-testid="savingsTypeButton"
              onClick={() => setSelectedPaymentType(PaymentType.SAVINGS)}
            >
              Savings account
            </PaymentTypeButton>
            <Box
              sx={{
                '&.MuiBox-root': {
                  mt: { mobile: 2, tablet: 3 },
                  display: 'flex',
                  flexDirection: { mobile: 'column', tablet: 'row' },
                  justifyContent: 'flex-end',
                },
              }}
            >
              <Button
                component={Link}
                relative="path"
                to=".."
                variant="outlined"
                color="inherit"
                data-testid="addPaymentMethodSelectCancel"
                sx={{
                  borderWidth: 2,
                  fontWeight: 700,
                  textTransform: 'none',
                  fontSize: 18,
                  color: 'text.secondary',
                  px: 4,
                  py: 0.5,
                }}
                disableRipple
                onClick={(e: MouseEvent) => {
                  e.preventDefault();
                  navigate(-1);
                }}
              >
                Cancel
              </Button>
            </Box>
          </>
        );
    }
  };

  const getPageTitle = () => {
    if (selectedPaymentType === PaymentType.CREDIT) {
      return 'debit/credit card';
    }
    if (selectedPaymentType) {
      return `${selectedPaymentType.toLowerCase()} account`;
    }

    return 'payment method';
  };

  return (
    <PaymentPage pageTitle={`Add ${getPageTitle()}`}>
      {!paymentOptionSelected && <Navigate to="/bills" replace />}
      {renderContent()}
    </PaymentPage>
  );
};

export default AddPaymentMethodPage;
